/**
 * StoreSync — mounts once in AppShell.
 * Bridges React Query + WebSocket data into the Zustand store.
 *
 * Key rules:
 * - Subscribe to actions only (via selectors) so this component never
 *   re-renders when store state changes — avoids infinite loops.
 * - Never use inline default values (= []) in destructuring — they create
 *   new references every render and break useEffect dependency checks.
 */
import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchPortfolio, fetchOrders, fetchChargesReport, fetchHoldings } from "@/api/client";
import { fetchPaperPositions } from "@/api/portfolioApi";
import { queryKeys } from "@/api/queryKeys";
import { useTradingStore } from "@/store/tradingStore";
import { useLiveTicks } from "@/contexts/LiveTicksContext";
import { totalUnrealisedPnl } from "@/utils/pnl";
import { isStateStoreEnabled } from "@/state/store";
import { usePortfolio } from "@/state/hooks/usePortfolioState";
import { useOrdersList } from "@/state/hooks/useOrdersState";
import { usePositionsList } from "@/state/hooks/usePositionsState";
import { useTradesSummary } from "@/state/hooks/useTradesState";

const PORTFOLIO_CACHE_KEY = "mct_portfolio_cache";
const LAST_SYNCED_KEY     = "mct_last_synced_at";

export default function StoreSync() {
  // Subscribe to actions only — these never change, so StoreSync never re-renders
  // due to store state updates (prevents infinite loops).
  const syncPortfolio    = useTradingStore(s => s.syncPortfolio);
  const syncOrders       = useTradingStore(s => s.syncOrders);
  const syncCharges      = useTradingStore(s => s.syncCharges);
  const syncHoldings     = useTradingStore(s => s.syncHoldings);
  const setSocketPnl     = useTradingStore(s => s.setSocketPnl);
  const setLtpUnrealised = useTradingStore(s => s.setLtpUnrealised);
  const setLastSyncedAt  = useTradingStore(s => s.setLastSyncedAt);
  const openPositionCount = useTradingStore(s => s.openPositionCount);

  const setWsStatus       = useTradingStore(s => s.setWsStatus);
  const setIndicesStatus  = useTradingStore(s => s.setIndicesStatus);
  const setAppStatus      = useTradingStore(s => s.setAppStatus);
  const setScanMonitor    = useTradingStore(s => s.setScanMonitor);
  const setBnTone         = useTradingStore(s => s.setBnTone);
  const setStockTones     = useTradingStore(s => s.setStockTones);
  const setScanComplete   = useTradingStore(s => s.setScanComplete);
  const addSystemAlert    = useTradingStore(s => s.addSystemAlert);

  const qc = useQueryClient();
  const {
    livePnl, ltpMap, status, watch,
    indicesStatus, appStatus, scanMonitor,
    bnTone, stockTones, scanComplete, systemAlerts,
  } = useLiveTicks();
  const prevStatusRef = useRef<string>("connecting");

  // Phase H: when state-store is on, the portfolio + orders snapshots come
  // from /api/state/* (in-memory, 3-4ms). StoreSync only needs the legacy
  // REST polls when state-store is disabled.
  const _storeOn      = isStateStoreEnabled();
  const _storePortfolio = usePortfolio();
  const _storePositions = usePositionsList();
  const _storeOrders    = useOrdersList();
  // E.1 fix: trades.summary carries today's wins/losses/count. Without this
  // useTradingStore showed wins=0/losses=0/trade_count=open_positions when
  // state-store was on (TradeQueue + Dashboard subtext consumed wrong values).
  const _storeTrades    = useTradesSummary();

  const { data: legacyPortfolio } = useQuery({
    queryKey: queryKeys.portfolio,
    queryFn: fetchPortfolio,
    enabled: !_storeOn,
  });

  const { data: legacyOrders } = useQuery({
    queryKey: queryKeys.orders,
    queryFn: () => fetchOrders(200),
    enabled: !_storeOn,
  });

  // Adapt state-store snapshots into the legacy portfolio/orders shape so
  // syncPortfolio/syncOrders consumers don't change.
  const portfolio: any = _storeOn && _storePortfolio
    ? {
        cash_balance:    _storePortfolio.cash?.cash_balance ?? 0,
        total_invested:  _storePortfolio.cash?.used_margin ?? 0,
        net_value:       (_storePortfolio.cash?.cash_balance ?? 0) + (_storePortfolio.cash?.used_margin ?? 0),
        realised_pnl:    _storePortfolio.pnl?.realised ?? 0,
        unrealised_pnl:  _storePortfolio.pnl?.unrealised ?? 0,
        net_pnl:         _storePortfolio.pnl?.net ?? 0,
        total_brokerage: _storePortfolio.pnl?.brokerage ?? 0,
        total_charges:   _storePortfolio.pnl?.brokerage ?? 0,
        // E.1 fix: wire wins/losses/trades from the trades domain summary
        // (today's closed round-trips) instead of hardcoded 0. trade_count
        // semantically matches orders.today.length (legacy /api/portfolio/
        // returned len(trades) — today's order count). _storeOrders.length
        // is the equivalent here.
        wins_count:      _storeTrades?.wins  ?? 0,
        losses_count:    _storeTrades?.losses ?? 0,
        trade_count:     _storeOrders?.length ?? 0,
        open_position_count: _storePortfolio.exposure?.total_open_positions ?? 0,
        positions:       _storePositions,
      }
    : legacyPortfolio;

  const orders: any = _storeOn ? _storeOrders : legacyOrders;

  // 2026-04-27 perf: when state-store is on, charges + holdings are pushed
  // via WS state_init/state_delta from the holdings + charges domains in
  // state_aggregator. The 60s/30s REST polls become wasted cycles. Gate
  // them on !_storeOn — same pattern as portfolio/orders. (Mirroring is
  // handled by queryCacheMirror.ts so legacy useQuery consumers still
  // receive data through React Query — just from the WS push instead of
  // their own REST poll.)
  const { data: charges } = useQuery({
    queryKey: queryKeys.chargesReport("today"),
    queryFn: () => fetchChargesReport("today"),
    refetchInterval: 60000,
    enabled: !_storeOn,
  });

  const { data: holdings } = useQuery({
    queryKey: queryKeys.holdings,
    queryFn: fetchHoldings,
    refetchInterval: 30000,
    enabled: !_storeOn,
  });

  // ── On mount: seed React Query cache + lastSyncedAt from localStorage ────────
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PORTFOLIO_CACHE_KEY);
      const ts  = parseInt(localStorage.getItem(LAST_SYNCED_KEY) ?? "0", 10);
      if (raw) {
        const cached = JSON.parse(raw);
        qc.setQueryData(queryKeys.portfolio, cached);
      }
      if (ts > 0) setLastSyncedAt(ts);
    } catch {
      // corrupt cache — ignore, live fetch will overwrite
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── WS reconnect → immediately refetch positions ──────────────────────────
  useEffect(() => {
    if (prevStatusRef.current !== "live" && status === "live") {
      qc.invalidateQueries({ queryKey: queryKeys.portfolio });
      qc.invalidateQueries({ queryKey: queryKeys.closedPositions });
    }
    prevStatusRef.current = status;
  }, [status, qc]);

  // Portfolio → store
  useEffect(() => {
    if (!portfolio) return;
    syncPortfolio({
      realisedPnl:        portfolio.realised_pnl,
      unrealisedPnl:      portfolio.unrealised_pnl,
      netPnl:             portfolio.net_pnl,
      cashBalance:        portfolio.cash_balance,
      totalInvested:      portfolio.total_invested,
      netValue:           portfolio.net_value,
      brokerage:          portfolio.total_brokerage,
      winsCount:          portfolio.wins_count        ?? 0,
      lossesCount:        portfolio.losses_count      ?? 0,
      tradeCount:         portfolio.trade_count,
      openPositionCount:  portfolio.open_position_count ?? 0,
      positions:          portfolio.positions,
    });
    // Persist for next app load
    try {
      localStorage.setItem(PORTFOLIO_CACHE_KEY, JSON.stringify(portfolio));
      localStorage.setItem(LAST_SYNCED_KEY, String(Date.now()));
    } catch { /* storage quota — ignore */ }
  }, [portfolio, syncPortfolio]);

  // Orders → store (undefined guard instead of inline [] default)
  useEffect(() => {
    syncOrders(orders ?? []);
  }, [orders, syncOrders]);

  // Charges → store
  useEffect(() => {
    if (charges) syncCharges(charges);
  }, [charges, syncCharges]);

  // Holdings → store
  useEffect(() => {
    syncHoldings(holdings ?? []);
  }, [holdings, syncHoldings]);

  // WebSocket portfolio_pnl → store (primary P&L source)
  useEffect(() => {
    if (!livePnl) return;
    setSocketPnl(livePnl);
  }, [livePnl, setSocketPnl]);

  // WS positions → store (updates position list between REST polls)
  // Only update the positions array; financial fields (totalInvested, netValue, brokerage)
  // stay from the last REST sync since they aren't in the WS payload.
  // Paper-mode override below replaces these when PAPER_TRADING_MODE=True.
  const setPositions = useTradingStore(s => s.setPositions);
  const isPaperMode = appStatus?.paper_mode ?? true;
  useEffect(() => {
    if (isPaperMode) return;   // paper-mode positions are managed by the paper feed below
    if (!livePnl?.positions?.length) return;
    setPositions(livePnl.positions as unknown as import('@/api/client').Position[]);
  }, [livePnl?.positions, setPositions, isPaperMode]);

  // Paper-mode positions feed — pulls from simulation.paper_positions every 5s
  // and pushes into the trading store. Replaces the Kite-side livePnl.positions
  // (which is empty in paper mode since Kite never sees paper trades).
  const { data: paperData } = useQuery({
    queryKey: queryKeys.paperPositions,
    queryFn: () => fetchPaperPositions(),
    enabled: !_storeOn && isPaperMode,
    refetchInterval: 5000,
    refetchOnMount: "always",
  });
  useEffect(() => {
    if (!isPaperMode) return;
    const list = ((paperData?.positions ?? []) as any[]).map((p: any) => ({
      symbol: p.symbol,
      tradingsymbol: p.symbol,
      quantity: p.quantity,
      average_price: p.avg_price,
      avg_price: p.avg_price,
      last_price: p.ltp,
      ltp: p.ltp,
      unrealised_pnl: p.unrealised_pnl,
      pnl: p.unrealised_pnl,
      instrument_type: p.instrument_type,
      product: "MIS",
      strategy: p.strategy,
      entry_order_id: p.order_id,
      opened_at: p.opened_at,
    }));
    setPositions(list as unknown as import('@/api/client').Position[]);
  }, [paperData, isPaperMode, setPositions]);

  // Watch position + holding symbols on WS for live LTP ticks.
  // StoreSync is always mounted — ensures watch fires regardless of which tab is active.
  const watchSymbols = [
    ...(portfolio?.positions?.filter((p: any) => p.quantity !== 0).map((p: any) => p.symbol) ?? []),
    ...(holdings?.map((h: { tradingsymbol: string }) => h.tradingsymbol) ?? []),
  ];
  const watchKey = [...new Set(watchSymbols)].sort().join(",");
  useEffect(() => {
    if (watchSymbols.length > 0 && status === "live") watch([...new Set(watchSymbols)]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchKey, status, watch]);

  // Live LTP ticks → unrealised P&L in store
  // Gated on openPositionCount (from Zerodha via REST or socket) — prevents stale MCT DB
  // positions from injecting fake unrealised after all positions are closed in Zerodha.
  useEffect(() => {
    if (openPositionCount === 0) return;
    if (!portfolio?.positions?.length) return;
    const open = portfolio.positions.filter((p: any) => p.quantity !== 0);
    if (!open.length) return;
    const unrealised = totalUnrealisedPnl(open, ltpMap as Record<string, number>);
    setLtpUnrealised(unrealised);
  }, [ltpMap, portfolio?.positions, openPositionCount, setLtpUnrealised]);

  // ── Bridge WS events → store ───────────────────────────────────────────────
  useEffect(() => { setWsStatus(status); }, [status, setWsStatus]);
  useEffect(() => { if (indicesStatus) setIndicesStatus(indicesStatus); }, [indicesStatus, setIndicesStatus]);
  useEffect(() => { if (appStatus) setAppStatus(appStatus); }, [appStatus, setAppStatus]);
  useEffect(() => { if (scanMonitor) setScanMonitor(scanMonitor); }, [scanMonitor, setScanMonitor]);
  useEffect(() => { if (bnTone) setBnTone(bnTone); }, [bnTone, setBnTone]);
  useEffect(() => { if (Object.keys(stockTones).length) setStockTones(stockTones); }, [stockTones, setStockTones]);
  useEffect(() => { if (scanComplete) setScanComplete(scanComplete); }, [scanComplete, setScanComplete]);
  // System alerts: bridge new arrivals by tracking last-seen timestamp
  const lastAlertTsRef = useRef(0);
  useEffect(() => {
    if (!systemAlerts.length) return;
    const newest = systemAlerts[0];
    if (newest.timestamp > lastAlertTsRef.current) {
      // Bridge all alerts newer than our last-seen timestamp
      for (const alert of systemAlerts) {
        if (alert.timestamp > lastAlertTsRef.current) {
          addSystemAlert(alert);
        }
      }
      lastAlertTsRef.current = newest.timestamp;
    }
  }, [systemAlerts, addSystemAlert]);

  return null;
}
